import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Back-to-LifeGuide control.
 *
 * Uses history when a same-origin referrer exists (so Back returns to the
 * exact page the user came from — e.g. Home → Gift → Back → Home). Falls
 * back safely:
 *   - Signed-in users → /home
 *   - Signed-out users → /welcome
 *
 * Never touches Twilio/SMS consent, Stripe checkout, auth, or the Proverb
 * handoff. Purely visual + client-side navigation.
 */
export default function BackToLifeGuide({
  label,
  fallback,
  className = "",
  testid = "back-to-lifeguide",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const safeFallback = fallback || (user ? "/home" : "/welcome");
  const resolvedLabel = label || (user ? "Back to LifeGuide" : "Back");

  const handleClick = (e) => {
    e.preventDefault();
    // Prefer same-origin history when we have any; otherwise fall back.
    let sameOrigin = false;
    try {
      sameOrigin = !!document.referrer && new URL(document.referrer).origin === window.location.origin;
    } catch {
      sameOrigin = false;
    }
    // React-Router doesn't expose a public history-length API; probing
    // window.history.length is unreliable across reloads. So the safest
    // heuristic is: if we came from a same-origin page in this tab, go
    // back one step; otherwise navigate to the fallback.
    if (sameOrigin && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(safeFallback);
    }
    // Guard: if `navigate(-1)` happens to land us outside the app (e.g.
    // the previous entry was an external OAuth redirect), the browser
    // will just move away — we can't intercept that from React. In the
    // 99% case the previous entry is a LifeGuide route.
    void location; // keep hook eslint-happy
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testid}
      className={
        className ||
        "inline-flex items-center gap-1.5 text-sm text-[#6B6B6B] hover:text-[#4F6F52] transition"
      }
    >
      <ArrowLeft size={14} strokeWidth={2} />
      {resolvedLabel}
    </button>
  );
}
